import { useState, useRef, useEffect } from 'react'
import { Modal } from '@/shared/components/ui'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { createParty } from '@/modules/registrations/contacts/contactsService'
import type { Party } from '@/modules/registrations/contacts/contactsTypes'

export interface PartyFormModalProps {
  unitId: string
  isOpen: boolean
  onClose: () => void
  /** Called with the newly created party after a successful save */
  onCreated: (party: Party) => void
}

interface FormData {
  name:     string
  whatsapp: string
  pix_key:  string
  notes:    string
}

const EMPTY: FormData = { name: '', whatsapp: '', pix_key: '', notes: '' }

const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50'

export function PartyFormModal({ unitId, isOpen, onClose, onCreated }: PartyFormModalProps) {
  const { user }           = useAuth()
  const { t }              = useTranslation()
  const tc                 = (key: string) => t(`registrations.contacts.${key}`)

  const [form,      setForm]      = useState<FormData>(EMPTY)
  const [errors,    setErrors]    = useState<Record<string, string>>({})
  const [saving,    setSaving]    = useState(false)
  const [apiError,  setApiError]  = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  // Reset form every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setForm(EMPTY)
      setErrors({})
      setApiError(null)
      setTimeout(() => nameRef.current?.focus(), 80)
    }
  }, [isOpen])

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!form.name.trim()) errs.name = tc('messages.nameRequired')
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleSave() {
    if (!validate()) return
    if (!user?.id) { setApiError('Usuário não identificado'); return }

    setSaving(true)
    setApiError(null)
    try {
      const created = await createParty(unitId, form, user.id)
      onCreated(created)
      onClose()
    } catch (err: any) {
      setApiError(err?.message ?? 'Erro ao criar contato')
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      const tag = (e.target as HTMLElement).tagName.toLowerCase()
      if (tag === 'textarea') return
      e.preventDefault()
      handleSave()
    }
    if (e.key === 'Escape') onClose()
  }

  const headerLeft = (
    <button
      type="button"
      onClick={onClose}
      className="flex items-center gap-2 text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-white transition-colors md:hidden"
      title={tc('actions.cancel')}
    >
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
    </button>
  )

  const headerRight = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 transition-colors disabled:opacity-40"
        title={tc('actions.create')}
      >
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </button>

      {/* Desktop-only close button (hidden on mobile) */}
      <button
        type="button"
        onClick={onClose}
        disabled={saving}
        className="hidden md:flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors disabled:opacity-40"
        title={tc('actions.cancel')}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={tc('record.new')} size="sm" headerLeft={headerLeft} headerRight={headerRight}>
      <div className="space-y-4" onKeyDown={handleKeyDown}>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('record.fields.name')} *
          </label>
          <input
            ref={nameRef}
            type="text"
            value={form.name}
            onChange={(e) => {
              setForm((f) => ({ ...f, name: e.target.value }))
              if (errors.name) setErrors((prev) => { const n = { ...prev }; delete n.name; return n })
            }}
            placeholder={tc('record.fields.namePlaceholder')}
            disabled={saving}
            className={INPUT_CLS}
          />
          {errors.name && <p className="mt-1 text-sm text-rose-600">{errors.name}</p>}
        </div>

        {/* WhatsApp */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('record.fields.whatsapp')}
          </label>
          <input
            type="tel"
            value={form.whatsapp}
            onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
            placeholder={tc('record.fields.whatsappPlaceholder')}
            disabled={saving}
            className={INPUT_CLS}
          />
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {tc('record.fields.whatsappHelp')}
          </p>
        </div>

        {/* Pix key */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('record.fields.pixKey')}
          </label>
          <input
            type="text"
            value={form.pix_key}
            onChange={(e) => setForm((f) => ({ ...f, pix_key: e.target.value }))}
            placeholder={tc('record.fields.pixKeyPlaceholder')}
            disabled={saving}
            className={`${INPUT_CLS} font-mono`}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {tc('record.fields.notes')}
          </label>
          <textarea
            rows={3}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder={tc('record.fields.notesPlaceholder')}
            disabled={saving}
            className={`${INPUT_CLS} resize-none`}
          />
        </div>

        {apiError && (
          <p className="text-sm text-rose-600 dark:text-rose-400">{apiError}</p>
        )}

      </div>
    </Modal>
  )
}
